import {
  Heart,
  Hand,
  Brain,
  Coffee,
  Utensils,
  Pill,
  Car,
  Footprints,
  Home,
  Stethoscope,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "../lib/constants";
import { SERVICE_CATALOG } from "../lib/serviceCatalog";
import { useReveal } from "../hooks/useReveal";
import { Link } from "react-router-dom";

const SLUG_MAP = {
  "Companionship Care": "companion-care",
  "Personal Care Assistance": "personal-care",
  "Dementia & Memory Support": "dementia-care",
  "Respite Care": "respite-care",
  "Meal Preparation": "meal-preparation",
  "Medication Reminders": "medication-reminders",
  "Transportation Assistance": "transportation",
  "Mobility Assistance": "mobility-assistance",
  "Light Housekeeping": "light-housekeeping",
  "Post-Hospital Support": "post-hospital-support",
};

const ICON_MAP = {
  Heart,
  Hand,
  Brain,
  Coffee,
  Utensils,
  Pill,
  Car,
  Footprints,
  Home,
  Stethoscope,
};

function ServiceCard({ s, index }) {
  const [ref, visible] = useReveal(0.1);
  const Icon = ICON_MAP[s.icon] || Heart;
  return (
    <article
      ref={ref}
      data-testid={`service-card-${index}`}
      className={`card-lift group bg-white rounded-2xl p-7 border border-[#E5DDC9]/60 shadow-[0_4px_20px_rgb(15,23,42,0.04)] ${
        visible ? "reveal visible" : "opacity-0"
      }`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="icon-pill w-14 h-14 rounded-2xl flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#AC9667]" strokeWidth={1.6} />
        </div>
        <span className="font-serif text-2xl text-[#E5DDC9] group-hover:text-[#AC9667] transition-colors">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>
      <h3 className="font-serif text-[26px] text-[#005165] mb-3 leading-tight">
        {s.title}
      </h3>
      <p className="text-[15px] text-[#4B5563] leading-relaxed mb-5">{s.blurb}</p>
      <Link
        to={`/services/${SLUG_MAP[s.title] || ""}`}
        data-testid={`service-learn-${index}`}
        className="inline-flex items-center gap-1.5 text-[13px] tracking-wider uppercase text-[#005165] hover:text-[#AC9667] transition-colors"
      >
        Learn More <ArrowUpRight className="w-4 h-4" />
      </Link>
    </article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#FFFFFF]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-14 md:mb-20">
          <span className="gold-divider mb-5">Our Services</span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-[#005165] leading-[1.08]">
            Personalized care, thoughtfully delivered <em className="not-italic text-[#AC9667]">at home.</em>
          </h2>
          <p className="mt-6 text-lg text-[#4B5563] max-w-2xl leading-relaxed">
            From a few hours of companionship to comprehensive daily support — every plan is
            built around the person, never a checklist.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.title} s={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
